import { Container, Graphics, Text, Sprite } from 'pixi.js';
import { AssetLoader } from './AssetLoader';
import { ScoreManager } from './ScoreManager';

export class StartScreen extends Container {
  private background!: Graphics;
  private aliciButton!: Container;
  private jasperButton!: Container;
  private scoreManager: ScoreManager;
  private onCatSelectedCallback?: (cat: 'alici' | 'jasper') => void;
  private readonly backgroundAlpha = 0.35;
  private logoSprite!: Sprite;
  private logoInitialScale = 1;
  private logoWiggleTimer = 0;
  private readonly logoWiggleSpeed = 2;
  private readonly logoWiggleAmount = 0.05;

  // Cat animation properties
  private aliciSprite!: Sprite;
  private jasperSprite!: Sprite;
  private catPulseTimer = 0;
  private readonly catPulseSpeed = 1.5;
  private readonly catPulseAmount = 0.05;
  private readonly catBaseScale = 0.6;

  // Selection animation properties
  private isAnimatingSelection = false;
  private selectedCat?: 'alici' | 'jasper';
  private selectionAnimationTimer = 0;
  private readonly selectionAnimationDuration = 0.3;

  constructor(gameWidth: number, gameHeight: number, scoreManager: ScoreManager) {
    super();

    this.scoreManager = scoreManager;

    this.createBackground(gameWidth, gameHeight);
    this.createTitle(gameWidth, gameHeight);
    this.createCatButtons(gameWidth, gameHeight);

    this.visible = true;
  }

  private createBackground(gameWidth: number, gameHeight: number): void {
    this.background = new Graphics();
    this.background.rect(0, 0, gameWidth, gameHeight);
    this.background.fill({ color: 0x000000, alpha: this.backgroundAlpha });
    this.addChild(this.background);
  }

  private createTitle(gameWidth: number, gameHeight: number): void {
    const assetLoader = AssetLoader.getInstance();
    const logoTexture = assetLoader.getTexture('logo');

    this.logoSprite = new Sprite(logoTexture);
    this.logoSprite.anchor.set(0.5);
    this.logoSprite.x = gameWidth / 2;
    this.logoSprite.y = gameHeight / 2;

    // Scale the logo to fit nicely (adjust as needed)
    const maxWidth = gameWidth * 0.6;
    const maxHeight = gameHeight * 0.3;
    const scale = Math.min(maxWidth / this.logoSprite.width, maxHeight / this.logoSprite.height);
    this.logoSprite.scale.set(scale);
    this.logoInitialScale = scale;

    this.addChild(this.logoSprite);
  }

  private createCatButtons(gameWidth: number, gameHeight: number): void {
    const assetLoader = AssetLoader.getInstance();

    const centerY = gameHeight * 0.5;
    const buttonSpacing = 250;

    const aliciBestScore = this.scoreManager.getBestScoreForCat('alici');
    const jasperBestScore = this.scoreManager.getBestScoreForCat('jasper');

    this.aliciButton = this.createCatButton(
      assetLoader.getTexture('alici_run_1'),
      gameWidth / 2 - buttonSpacing,
      centerY,
      'alici',
      aliciBestScore
    );

    this.jasperButton = this.createCatButton(
      assetLoader.getTexture('jasper_run_1'),
      gameWidth / 2 + buttonSpacing,
      centerY,
      'jasper',
      jasperBestScore
    );

    // Store sprite references for animation
    this.aliciSprite = this.aliciButton.children[0] as Sprite;
    this.jasperSprite = this.jasperButton.children[0] as Sprite;

    this.addChild(this.aliciButton);
    this.addChild(this.jasperButton);
  }

  private createCatButton(
    texture: any,
    x: number,
    y: number,
    catType: 'alici' | 'jasper',
    bestScore: number
  ): Container {
    const button = new Container();

    const catSprite = new Sprite(texture);
    catSprite.anchor.set(0.5);
    catSprite.scale.set(0.6);
    catSprite.y = -30;

    const bestScoreText = new Text({
      text: bestScore > 0 ? `Best: ${bestScore}` : '',
      style: {
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 'normal',
        fill: bestScore > 0 ? 0xffd700 : 0x888888,
        align: 'center',
      },
    });

    bestScoreText.anchor.set(0.5);
    bestScoreText.y = 78;

    button.addChild(catSprite);
    button.addChild(bestScoreText);

    button.x = x;
    button.y = y;

    button.eventMode = 'static';
    button.cursor = 'pointer';

    button.on('pointerdown', () => {
      if (!this.isAnimatingSelection) {
        this.startSelectionAnimation(catType);
      }
    });

    return button;
  }

  private startSelectionAnimation(catType: 'alici' | 'jasper'): void {
    this.isAnimatingSelection = true;
    this.selectedCat = catType;
    this.selectionAnimationTimer = 0;
  }

  update(deltaTime: number): void {
    if (!this.visible) return;

    // Logo wiggle animation (only when not selecting)
    if (!this.isAnimatingSelection) {
      this.logoWiggleTimer += deltaTime;
      const wiggle = Math.sin(this.logoWiggleTimer * this.logoWiggleSpeed) * this.logoWiggleAmount;
      this.logoSprite.rotation = wiggle;
    }

    // Selection animation
    if (this.isAnimatingSelection) {
      this.selectionAnimationTimer += deltaTime;
      const progress = Math.min(this.selectionAnimationTimer / this.selectionAnimationDuration, 1);

      const selectedButton = this.selectedCat === 'alici' ? this.aliciButton : this.jasperButton;
      const selectedSprite = this.selectedCat === 'alici' ? this.aliciSprite : this.jasperSprite;
      const otherButton = this.selectedCat === 'alici' ? this.jasperButton : this.aliciButton;
      const otherSprite = this.selectedCat === 'alici' ? this.jasperSprite : this.aliciSprite;

      // Selected cat: scale up and fade out
      const selectedScale = this.catBaseScale + progress * 0.4; // Scale from 0.6 to 1.0
      selectedSprite.scale.set(selectedScale);
      selectedButton.alpha = 1 - progress;

      // Other cat: scale down to 0 with rotation and fade out
      const otherScale = this.catBaseScale * (1 - progress); // Scale from 0.6 to 0
      otherSprite.scale.set(otherScale);
      otherSprite.rotation = progress * Math.PI * 2; // Full rotation (360 degrees)
      otherButton.alpha = 1 - progress;

      // Logo: scale down to 0 with rotation and fade out
      const logoScale = this.logoInitialScale * (1 - progress); // Scale from initial to 0
      this.logoSprite.scale.x = logoScale;
      this.logoSprite.scale.y = logoScale;
      this.logoSprite.rotation = progress * Math.PI * 1.5; // 270 degrees rotation
      this.logoSprite.alpha = 1 - progress;

      // When animation completes, trigger callback
      if (progress >= 1 && this.onCatSelectedCallback && this.selectedCat) {
        this.onCatSelectedCallback(this.selectedCat);
      }
    } else {
      // Cat pulsating scale animation (only when not selecting)
      this.catPulseTimer += deltaTime;
      const pulse = Math.sin(this.catPulseTimer * this.catPulseSpeed) * this.catPulseAmount;
      const scale = this.catBaseScale * (1 + pulse);

      if (this.aliciSprite) {
        this.aliciSprite.scale.set(scale);
      }
      if (this.jasperSprite) {
        this.jasperSprite.scale.set(scale);
      }
    }
  }

  show(): void {
    this.visible = true;
    this.updateBestScores();
    this.resetAnimationState();
  }

  private resetAnimationState(): void {
    this.isAnimatingSelection = false;
    this.selectedCat = undefined;
    this.selectionAnimationTimer = 0;

    // Reset logo
    if (this.logoSprite) {
      this.logoSprite.rotation = 0;
      this.logoSprite.alpha = 1;
      const maxWidth = this.logoSprite.texture.width * 0.6;
      const maxHeight = this.logoSprite.texture.height * 0.3;
      const scale = Math.min(maxWidth / this.logoSprite.texture.width, maxHeight / this.logoSprite.texture.height);
      this.logoSprite.scale.set(scale);
    }

    if (this.aliciButton) {
      this.aliciButton.alpha = 1;
      const aliciSprite = this.aliciButton.children[0] as Sprite;
      if (aliciSprite) {
        aliciSprite.scale.set(this.catBaseScale);
        aliciSprite.rotation = 0;
      }
    }

    if (this.jasperButton) {
      this.jasperButton.alpha = 1;
      const jasperSprite = this.jasperButton.children[0] as Sprite;
      if (jasperSprite) {
        jasperSprite.scale.set(this.catBaseScale);
        jasperSprite.rotation = 0;
      }
    }
  }

  hide(): void {
    this.visible = false;
  }

  private updateBestScores(): void {
    const aliciBestScore = this.scoreManager.getBestScoreForCat('alici');
    const jasperBestScore = this.scoreManager.getBestScoreForCat('jasper');

    if (this.aliciButton && this.aliciButton.children[1]) {
      const aliciBestScoreText = this.aliciButton.children[1] as Text;
      aliciBestScoreText.text = aliciBestScore > 0 ? `Best: ${aliciBestScore}` : '';
      aliciBestScoreText.style.fill = aliciBestScore > 0 ? 0xffd700 : 0x888888;
    }

    if (this.jasperButton && this.jasperButton.children[1]) {
      const jasperBestScoreText = this.jasperButton.children[1] as Text;
      jasperBestScoreText.text = jasperBestScore > 0 ? `Best: ${jasperBestScore}` : '';
      jasperBestScoreText.style.fill = jasperBestScore > 0 ? 0xffd700 : 0x888888;
    }
  }

  onCatSelected(callback: (cat: 'alici' | 'jasper') => void): void {
    this.onCatSelectedCallback = callback;
  }

  updateDimensions(gameWidth: number, gameHeight: number): void {
    if (this.background) {
      this.background.clear();
      this.background.rect(0, 0, gameWidth, gameHeight);
      this.background.fill({ color: 0x000000, alpha: this.backgroundAlpha });
    }

    if (this.logoSprite) {
      this.logoSprite.x = gameWidth / 2;
      this.logoSprite.y = gameHeight / 2;

      const maxWidth = gameWidth * 0.6;
      const maxHeight = gameHeight * 0.3;
      const scale = Math.min(maxWidth / this.logoSprite.texture.width, maxHeight / this.logoSprite.texture.height);
      this.logoSprite.scale.set(scale);
      this.logoInitialScale = scale;
    }

    const centerY = gameHeight * 0.5;
    const buttonSpacing = 250;

    if (this.aliciButton) {
      this.aliciButton.x = gameWidth / 2 - buttonSpacing;
      this.aliciButton.y = centerY;
    }

    if (this.jasperButton) {
      this.jasperButton.x = gameWidth / 2 + buttonSpacing;
      this.jasperButton.y = centerY;
    }
  }
}
